/** Task-level owner chip + reassign dialog (A1). Own actions can be handed over by their owner; the Head of Legal can reassign any. */
import { useEffect, useState } from "react";
import { Loader2, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getAgeingHandlers, sortNames, setLegalEventOwner } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

export function OwnerChip({ ev, onChanged, compact = true }) {
  const [open, setOpen] = useState(false);
  const owner = ev.owner;
  const pinned = ev.owner_pinned;
  if (ev.status !== "scheduled") {
    return owner ? <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5"><UserRound className="w-2.5 h-2.5" />{owner}</span> : null;
  }
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 hover:bg-slate-100 ${owner ? "bg-white text-slate-700 border-slate-300" : "bg-rose-50 text-rose-700 border-rose-200"}`}
        title={pinned ? "Reassigned individually — does not follow the case owner" : "Owner follows the case's legal handler / account owner"}
        data-testid={`owner-chip-${ev.event_id}`}>
        <UserRound className="w-2.5 h-2.5" />{owner || "Unassigned"}{pinned && <span className="text-[8px] uppercase text-indigo-700">pinned</span>}
      </button>
      {open && <ReassignDialog ev={ev} onClose={() => setOpen(false)} onDone={async () => { setOpen(false); await onChanged?.(); }} />}
    </>
  );
}

export function ReassignDialog({ ev, onClose, onDone }) {
  const [handlers, setHandlers] = useState([]);
  const [owner, setOwner] = useState(ev.owner || "__unassigned__");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getAgeingHandlers("legal").then((h) => setHandlers(sortNames(h.handlers, ev.owner))).catch(() => {}); }, [ev.owner]);
  const submit = async () => {
    setBusy(true);
    try {
      await setLegalEventOwner(ev.event_id, owner === "__unassigned__" ? null : owner, reason.trim());
      toast.success(`Reassigned to ${owner === "__unassigned__" ? "Unassigned" : owner}`);
      await onDone?.();
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>Reassign action</DialogTitle>
          <DialogDescription>{ev.label || ev.custom_label || ev.type} · {ev.date}. Current owner: <strong>{ev.owner || "Unassigned"}</strong>. The handover is logged on the case timeline.</DialogDescription>
        </DialogHeader>
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger data-testid="reassign-owner-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__unassigned__">Unassigned (goes to the Unassigned panel)</SelectItem>
            {handlers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the handover (5+ characters)" data-testid="reassign-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 5 || (owner === "__unassigned__" ? !ev.owner : owner === ev.owner)} data-testid="reassign-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Reassign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
